import { useState } from "react";
import { ArrowRight, ExternalLink } from "lucide-react";
import { tagClass } from "@/lib/tags";
import { classifyPhrase, getAdminToken } from "@/lib/api";
import { toast } from "sonner";

// Per-variant styling — explicit lookup instead of nested ternaries.
const VARIANT_STYLES = {
  dark: {
    accent: "#F87171",
    panelClass: "panel panel-dark panel-hover",
    symbol: "⛧",
  },
  light: {
    accent: "#6EE7B7",
    panelClass: "panel panel-light panel-hover",
    symbol: "✦",
  },
};
const NEUTRAL_STYLE = {
  accent: "var(--primary)",
  panelClass: "panel panel-hover",
  symbol: null,
};

export default function ResultRow({ item, cipherLabel, idx, variant, onReclassified }) {
  const [open, setOpen] = useState(false);
  const isNarrative = variant === "dark" || variant === "light";
  const { accent, panelClass, symbol } = VARIANT_STYLES[variant] || NEUTRAL_STYLE;
  const rankStyle = idx === 0 && isNarrative ? { color: accent } : undefined;
  const rankColor = idx === 0 && !isNarrative ? "text-[var(--primary)]" : "muted";
  const analysisHref = `/?phrase=${encodeURIComponent(item.id)}`;
  const currentCategory = variant || "neutral";

  const reclassify = async (category) => {
    try {
      await classifyPhrase(item.id, category);
      toast.success(`"${item.text}" moved to ${category}`);
      onReclassified?.();
    } catch {
      toast.error("Reclassify failed — check admin token");
    }
  };

  return (
    <div className={panelClass} data-testid={`${isNarrative ? variant + "-" : ""}result-row-${idx}`}>
      <div className="p-5 flex items-start gap-5">
        <div className={`font-mono text-xs w-8 pt-2 shrink-0 ${rankColor}`} style={rankStyle}>
          {symbol && <span className="block leading-none mb-0.5">{symbol}</span>}
          #{String(idx + 1).padStart(2, "0")}
        </div>
        <div className="flex-1 min-w-0">
          <a
            href={analysisHref}
            target="_blank"
            rel="noopener noreferrer"
            className="font-display text-2xl tracking-tight break-words transition-colors inline-flex items-baseline gap-2 group"
            data-testid={`result-link-${idx}`}
            title="Open full analysis in new tab"
            onMouseEnter={(e) => { e.currentTarget.style.color = accent; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = ""; }}
          >
            <span className={isNarrative ? "font-semibold" : ""}>{item.text}</span>
            <ExternalLink size={14} className="shrink-0 translate-y-[2px] muted" />
          </a>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {item.matched_ciphers.map((c) => (
              <span key={c} className="tag tag-warm">{cipherLabel[c] || c}</span>
            ))}
            {item.tags.slice(0, 6).map((t) => (
              <span key={t} className={tagClass(t)}>{t}</span>
            ))}
          </div>
          {item.notes && (
            <div className="mt-2 px-2 py-1.5 bg-[rgba(212,175,55,0.06)] border-l-2 border-[var(--primary)] text-[var(--primary)] text-xs italic font-mono leading-relaxed">
              {item.notes}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="text-right">
            <div className="font-mono text-[10px] muted uppercase tracking-wider">matches</div>
            <div className="big-number text-3xl font-mono" style={isNarrative ? { color: accent } : undefined}>{item.match_count}</div>
          </div>
          <button
            onClick={() => setOpen((v) => !v)}
            className="font-mono text-[10px] muted hover:text-white uppercase tracking-wider flex items-center gap-1"
            data-testid={`expand-row-${idx}`}
          >
            {open ? "− Hide" : "+ Ciphers"}
            <ArrowRight size={10} className={`transition-transform ${open ? "rotate-90 text-[var(--primary)]" : ""}`} />
          </button>
          {getAdminToken() && (
            <div className="flex items-center gap-1" title="Admin: reclassify this phrase" data-testid={`reclassify-${idx}`}>
              {[
                { cat: "dark", sym: "⛧", color: "#F87171" },
                { cat: "neutral", sym: "○", color: "#9CA3AF" },
                { cat: "light", sym: "✦", color: "#6EE7B7" },
              ].map(({ cat, sym, color }) => (
                <button
                  key={cat}
                  onClick={() => reclassify(cat)}
                  disabled={cat === currentCategory}
                  className="text-xs px-1.5 py-0.5 border border-white/10 hover:border-white/40 transition-colors disabled:opacity-25 disabled:cursor-default"
                  style={{ color }}
                  title={cat === currentCategory ? `Already ${cat}` : `Move to ${cat}`}
                  data-testid={`reclassify-${cat}-${idx}`}
                >
                  {sym}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 px-5 py-4 bg-black/30">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] muted mb-3">
            Full cipher fingerprint
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-1 font-mono text-xs">
            {Object.entries(item.ciphers).map(([k, v]) => {
              const matched = item.matched_ciphers.includes(k);
              return (
                <div
                  key={k}
                  className={`flex justify-between border-b border-white/5 py-1.5 ${
                    matched ? "text-[var(--primary)]" : ""
                  }`}
                >
                  <span className="muted text-[11px] pr-2">{cipherLabel[k] || k}</span>
                  <span className={matched ? "font-semibold" : ""}>{v}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
